import { captureException } from '@sentry/react';
import { Component, ErrorInfo, ReactNode } from 'react';
import { Navigate } from 'react-router';

import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import posthog from '@/config/analytics/posthog';
import { ServiceUnavailableError } from '@/utils/errors';
import { ROUTES } from '@/utils/routeUtils';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
  hasError: boolean;
  isServiceUnavailable: boolean;
}

/**
 * RouterAwareErrorBoundary must be placed inside a Router so it can render
 * <Navigate> when a ServiceUnavailableError is thrown anywhere in the tree.
 * All other errors fall back to a generic "Something went wrong" UI.
 */
class RouterAwareErrorBoundary extends Component<Props, State> {
  public override state: State = {
    error: null,
    hasError: false,
    isServiceUnavailable: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      error,
      hasError: true,
      isServiceUnavailable: error instanceof ServiceUnavailableError,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    if (!(error instanceof ServiceUnavailableError)) {
      posthog.capture(ANALYTICS_EVENTS.ERROR_BOUNDARY_TRIGGERED, {
        boundary: 'router',
        error: error.message,
      });
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  public override render() {
    if (this.state.isServiceUnavailable) {
      // Route into the login flow so the user re-selects an org and a valid deployment
      // is re-saved, rather than dead-ending on the SERVICE_UNAVAILABLE page. Consistent
      // with queryClient's reset→login handling of ServiceUnavailableError.
      return <Navigate replace to={ROUTES.LOGIN} />;
    }

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-viewport bg-back-bg2 flex flex-col items-center justify-center px-4 text-center">
          <div className="border-border-subtle bg-card w-full max-w-md rounded-lg border p-8 shadow-md">
            <h2 className="text-secondary mb-4 text-2xl font-bold">Something went wrong</h2>
            <p className="text-tertiary mb-6">
              We encountered an unexpected error. Please try reloading the add-in.
            </p>
            {this.state.error && (
              <div className="bg-destructive-bg mb-6 max-h-32 overflow-auto rounded p-3 text-left">
                <code className="text-destructive text-xs">{this.state.error.toString()}</code>
              </div>
            )}
            <button
              className="bg-primary-active text-primary-on-button hover:bg-primary-hover w-full rounded-md px-4 py-2 font-medium transition-colors"
              onClick={this.handleReload}
            >
              Reload Add-in
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouterAwareErrorBoundary;
